import React, { useState, useEffect } from "react";
import CustomerLayout from "./CustomerLayout";
import { API_URL } from "../config";
import {
    Plane,
    Calendar,
    DollarSign,
    Award,
    Search,
    Download,
    Eye,
    Edit3,
    Ban,
    Trash2,
    ArrowRight,
    Users,
    Briefcase,
    Loader2,
    X,
    User,
    Mail,
    Phone,
    Globe,
    FileText,
    Luggage,
    CheckCircle,
    AlertTriangle,
    Clock,
    CreditCard,
    RefreshCw,
    Info,
} from "lucide-react";

// ============================================================
// INTERFACES
// ============================================================
interface Flight {
    id: number;
    airline: string;
    flightNumber: string;
    from: string;
    to: string;
    departureDate: string;
    returnDate: string;
    bookingDate: string;
    amount: number | null;
    status: "Confirmed" | "Pending" | "Processing" | "Cancelled" | "Completed";
    passengers: number;
    class: string;
}

interface FlightDetail extends Flight {
    referenceCode: string;
    tripType: string;
    via: string;
    flexibility: string;
    baggage: string;
    adults: number;
    children: number;
    infants: number;
    passenger: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        nationality: string;
        passportNumber: string;
        passportExpiry: string;
        addressLine1?: string;
        addressLine2?: string;
        state?: string;
        zipCode?: string;
    };
    specialRequests: string;
}

interface RawFlightRequest {
    id: number;
    user_id: number | null;
    reference_code: string;
    trip_type: string;
    trip_class: string;
    from_location: string;
    to_location: string;
    via_location: string | null;
    depart_date: string;
    return_date: string | null;
    flexibility: string;
    baggage: string;
    preferred_airline: string | null;
    price: string | number | null;
    assigned_airline: string | null;
    adults_count: number;
    children_count: number;
    infants_count: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    nationality: string;
    passport_number: string;
    passport_expiry: string;
    special_requests: string | null;
    admin_notes: string | null;
    payment_link: string | null;
    payment_status: string;
    status: string;
    created_at: string;
    updated_at: string;
}

// ============================================================
// FONCTIONS UTILITAIRES
// ============================================================
function capitalize(s: string): Flight["status"] {
    if (!s) return "Pending";
    return (s.charAt(0).toUpperCase() + s.slice(1)) as Flight["status"];
}

function mapToFlight(raw: RawFlightRequest): Flight {
    return {
        id: raw.id,
        airline: raw.assigned_airline || raw.preferred_airline || "Airline pending assignment",
        flightNumber: raw.reference_code,
        from: raw.from_location,
        to: raw.to_location,
        departureDate: raw.depart_date,
        returnDate: raw.return_date ?? "",
        bookingDate: raw.created_at,
        amount: raw.price !== null && raw.price !== undefined && raw.price !== "" ? Number(raw.price) : null,
        status: capitalize(raw.status),
        passengers: (raw.adults_count ?? 0) + (raw.children_count ?? 0) + (raw.infants_count ?? 0),
        class: raw.trip_class,
    };
}

function mapToFlightDetail(raw: RawFlightRequest): FlightDetail {
    return {
        ...mapToFlight(raw),
        referenceCode: raw.reference_code,
        tripType: raw.trip_type,
        via: raw.via_location || "Direct",
        flexibility: raw.flexibility,
        baggage: raw.baggage,
        adults: raw.adults_count,
        children: raw.children_count,
        infants: raw.infants_count,
        passenger: {
            firstName: raw.first_name,
            lastName: raw.last_name,
            email: raw.email,
            phone: raw.phone,
            nationality: raw.nationality,
            passportNumber: raw.passport_number,
            passportExpiry: raw.passport_expiry,
        },
        specialRequests: raw.special_requests || "None",
    };
}

function airportCode(location?: string | null): string {
    if (!location) return "N/A";
    const match = location.match(/\(([^)]+)\)\s*$/);
    if (match) return match[1];
    return location.split(",")[0].trim();
}

function extractArray<T>(payload: unknown): T[] {
    if (Array.isArray(payload)) return payload as T[];
    if (payload && typeof payload === "object" && Array.isArray((payload as any).data)) {
        return (payload as any).data as T[];
    }
    return [];
}

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const MyFlights: React.FC = () => {
    const [flights, setFlights] = useState<Flight[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeFilter, setActiveFilter] = useState("All Flights");

    // Modal Details
    const [selectedFlight, setSelectedFlight] = useState<FlightDetail | null>(null);
    const [modalLoading, setModalLoading] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    // Cancel Modal
    const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
    const [flightToCancel, setFlightToCancel] = useState<Flight | null>(null);
    const [isCancelling, setIsCancelling] = useState<boolean>(false);
    const [cancelError, setCancelError] = useState<string | null>(null);

    // Delete Modal
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [flightToDelete, setFlightToDelete] = useState<Flight | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Edit Modal
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [flightToEdit, setFlightToEdit] = useState<FlightDetail | null>(null);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [updateError, setUpdateError] = useState<string | null>(null);

    const [editFormData, setEditFormData] = useState({
        fullName: "",
        email: "",
        phone: "",
        addressLine1: "",
        addressLine2: "",
        state: "",
        zipCode: "",
        specialRequests: ""
    });

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        });
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'confirmed': return { bg: '#e6f4ea', text: '#1e7e34', dot: '#1e7e34' };
            case 'pending': return { bg: '#fef3d6', text: '#b25900', dot: '#b25900' };
            case 'processing': return { bg: '#e8f0fe', text: '#1a73e8', dot: '#1a73e8' };
            case 'cancelled': return { bg: '#fce8e6', text: '#c5221f', dot: '#c5221f' };
            case 'completed': return { bg: '#f0ece4', text: '#294b5a', dot: '#294b5a' };
            default: return { bg: '#f0f0f0', text: '#666', dot: '#666' };
        }
    };

    // ============================================================
    // FETCH FLIGHTS
    // ============================================================
    useEffect(() => {
        const fetchFlights = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await fetch(`${API_URL}/customer/my-flights`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                    },
                });

                if (!response.ok) {
                    throw new Error("Failed to fetch your flight bookings.");
                }

                const payload = await response.json();
                const rawList = extractArray<RawFlightRequest>(payload);
                setFlights(rawList.map(mapToFlight));
            } catch (err: any) {
                console.error("Fetch Flights Error:", err);
                setError(err.message || "Unable to load bookings.");
                setFlights([]);
            } finally {
                setLoading(false);
            }
        };

        fetchFlights();
    }, []);

    // ============================================================
    // VIEW DETAILS
    // ============================================================
    const handleViewDetails = async (id: number) => {
        setIsModalOpen(true);
        setModalLoading(true);
        try {
            const response = await fetch(`${API_URL}/customer/flights/${id}`, {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                },
            });

            if (!response.ok) {
                throw new Error("Unable to load flight details.");
            }

            const payload = await response.json();
            const raw: RawFlightRequest = payload?.data ?? payload;
            setSelectedFlight(mapToFlightDetail(raw));
        } catch (err: any) {
            alert(err.message || "Loading error.");
            setIsModalOpen(false);
        } finally {
            setModalLoading(false);
        }
    };

    // ============================================================
    // EDIT FLIGHT
    // ============================================================
    const handleEditClick = async (flight: Flight | FlightDetail) => {
        if (flight.status !== "Pending") return;

        setUpdateError(null);
        setIsModalOpen(false);
        setIsEditModalOpen(true);

        if ('passenger' in flight && flight.passenger) {
            setFlightToEdit(flight as FlightDetail);
            setEditFormData({
                fullName: `${flight.passenger.firstName || ''} ${flight.passenger.lastName || ''}`.trim(),
                email: flight.passenger.email || "",
                phone: flight.passenger.phone || "",
                addressLine1: flight.passenger.addressLine1 || "",
                addressLine2: flight.passenger.addressLine2 || "",
                state: flight.passenger.state || "",
                zipCode: flight.passenger.zipCode || "",
                specialRequests: flight.specialRequests || ""
            });
            return;
        }

        setModalLoading(true);
        try {
            const response = await fetch(`${API_URL}/customer/flights/${flight.id}`, {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                },
            });

            if (!response.ok) {
                throw new Error("Unable to load data for editing.");
            }

            const payload = await response.json();
            const raw: RawFlightRequest = payload?.data ?? payload;
            const data: FlightDetail = mapToFlightDetail(raw);
            setFlightToEdit(data);
            setEditFormData({
                fullName: `${data.passenger.firstName || ''} ${data.passenger.lastName || ''}`.trim(),
                email: data.passenger.email || "",
                phone: data.passenger.phone || "",
                addressLine1: data.passenger.addressLine1 || "",
                addressLine2: data.passenger.addressLine2 || "",
                state: data.passenger.state || "",
                zipCode: data.passenger.zipCode || "",
                specialRequests: data.specialRequests || ""
            });
        } catch (err: any) {
            setUpdateError(err.message || "Loading error.");
        } finally {
            setModalLoading(false);
        }
    };

    const handleUpdateSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!flightToEdit) return;

        setIsUpdating(true);
        setUpdateError(null);

        try {
            const response = await fetch(`${API_URL}/customer/flights/${flightToEdit.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                },
                body: JSON.stringify(editFormData),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to update booking.");
            }

            setIsEditModalOpen(false);
            setFlightToEdit(null);
            
            // Refresh the list
            const updatedList = flights.map((f) => f.id === flightToEdit.id ? { ...f } : f);
            setFlights(updatedList);
        } catch (err: any) {
            setUpdateError(err.message || "Update error.");
        } finally {
            setIsUpdating(false);
        }
    };

    // ============================================================
    // CANCEL FLIGHT
    // ============================================================
    const openCancelModal = (flight: Flight) => {
        setFlightToCancel(flight);
        setCancelError(null);
        setIsCancelModalOpen(true);
    };

    const confirmCancel = async () => {
        if (!flightToCancel) return;

        setIsCancelling(true);
        setCancelError(null);

        try {
            const response = await fetch(`${API_URL}/customer/flights/${flightToCancel.id}/cancel`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to cancel booking.");
            }

            setFlights((prev) =>
                prev.map((f) => (f.id === flightToCancel.id ? { ...f, status: "Cancelled" } : f))
            );
            setIsCancelModalOpen(false);
            setFlightToCancel(null);
        } catch (err: any) {
            setCancelError(err.message || "Cancellation error.");
        } finally {
            setIsCancelling(false);
        }
    };

    // ============================================================
    // DELETE FLIGHT
    // ============================================================
    const openDeleteModal = (flight: Flight) => {
        setFlightToDelete(flight);
        setDeleteError(null);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!flightToDelete) return;

        setIsDeleting(true);
        setDeleteError(null);

        try {
            const response = await fetch(`${API_URL}/customer/flights/${flightToDelete.id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to delete booking.");
            }

            setFlights((prev) => prev.filter((flight) => flight.id !== flightToDelete.id));
            setIsDeleteModalOpen(false);
            setFlightToDelete(null);
        } catch (err: any) {
            setDeleteError(err.message || "Deletion error.");
        } finally {
            setIsDeleting(false);
        }
    };

    // ============================================================
    // STATS
    // ============================================================
    const totalFlights = flights.length;
    const upcomingFlights = flights.filter(
        (f) => f.status === "Confirmed" && f.departureDate && new Date(f.departureDate) >= new Date()
    ).length;
    const totalSpent = flights.reduce((sum, f) => sum + (f.amount ?? 0), 0);

    const completedFlights = flights.filter(f => f.status === "Completed").length;


    const filteredFlights = flights.filter((flight) => {
        const airline = flight.airline ?? "";
        const flightNumber = flight.flightNumber ?? "";
        const from = flight.from ?? "";
        const to = flight.to ?? "";

        const matchesSearch =
            airline.toLowerCase().includes(searchTerm.toLowerCase()) ||
            flightNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
            from.toLowerCase().includes(searchTerm.toLowerCase()) ||
            to.toLowerCase().includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;

        if (activeFilter === "Upcoming") {
            return flight.status === "Confirmed" && flight.departureDate && new Date(flight.departureDate) >= new Date();
        }
        if (activeFilter === "Completed") return flight.status === "Completed";
        if (activeFilter === "Cancelled") return flight.status === "Cancelled";

        return true;
    });

    // ============================================================
    // RENDER
    // ============================================================
    return (
        <CustomerLayout>
            <style>{`
                .flights-page {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 24px 40px;
                }

                .page-header-modern {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 32px;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .page-header-modern p {
                    font-size: 15px;
                    color: #6b7280;
                    margin: 4px 0 0 0;
                }

                .header-actions {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                }

                .btn-refresh {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: #ffffff;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    font-size: 13px;
                    font-weight: 600;
                    color: #374151;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .btn-refresh:hover {
                    background: #f9fafb;
                    border-color: #d1d5db;
                }

                .stats-grid-modern {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                    margin-bottom: 32px;
                }

                .stat-card-modern {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 20px 24px;
                    border: 1px solid #f0f0f0;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    transition: all 0.3s ease;
                }

                .stat-card-modern:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 24px rgba(0,0,0,0.06);
                }

                .stat-icon-modern {
                    width: 48px;
                    height: 48px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .stat-content-modern h3 {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 2px 0;
                    line-height: 1.2;
                }

                .stat-content-modern p {
                    font-size: 13px;
                    color: #6b7280;
                    margin: 0;
                }

                .filters-modern {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 16px 20px;
                    border: 1px solid #f0f0f0;
                    display: flex;
                    flex-wrap: wrap;
                    gap: 16px;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 24px;
                }

                .search-modern {
                    position: relative;
                    flex: 1;
                    min-width: 240px;
                }

                .search-modern input {
                    width: 100%;
                    padding: 10px 14px 10px 40px;
                    border-radius: 10px;
                    border: 1px solid #e5e7eb;
                    background: #f9fafb;
                    font-size: 14px;
                    outline: none;
                    color: #1a1a2e;
                    transition: all 0.2s ease;
                    box-sizing: border-box;
                }

                .search-modern input:focus {
                    border-color: #294b5a;
                    background: #ffffff;
                    box-shadow: 0 0 0 4px rgba(41, 75, 90, 0.08);
                }

                .search-modern svg {
                    position: absolute;
                    left: 13px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #9ca3af;
                }

                .filter-group {
                    display: flex;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .filter-btn-modern {
                    padding: 8px 18px;
                    border: 1px solid #e5e7eb;
                    border-radius: 20px;
                    background: #ffffff;
                    font-size: 13px;
                    font-weight: 500;
                    color: #6b7280;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .filter-btn-modern:hover {
                    background: #f9fafb;
                    border-color: #d1d5db;
                }

                .filter-btn-modern.active {
                    background: #294b5a;
                    color: #ffffff;
                    border-color: #294b5a;
                }

                .flights-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 16px;
                }

                .flight-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 20px 24px;
                    border: 1px solid #f0f0f0;
                    display: grid;
                    grid-template-columns: 1fr 1fr 1fr auto;
                    align-items: center;
                    gap: 16px;
                    transition: all 0.3s ease;
                }

                .flight-card:hover {
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    border-color: #e5e7eb;
                }

                .flight-card-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .flight-airline-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    background: #f3f4f6;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .flight-info h4 {
                    font-size: 15px;
                    font-weight: 600;
                    color: #1a1a2e;
                    margin: 0 0 3px 0;
                }

                .flight-info .flight-ref {
                    font-size: 12px;
                    color: #6b7280;
                    font-family: monospace;
                }

                .flight-route-modern {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .route-point {
                    text-align: center;
                }

                .route-point .city {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1a1a2e;
                }

                .route-point .code {
                    font-size: 11px;
                    color: #6b7280;
                    font-weight: 500;
                }

                .route-line {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    min-width: 40px;
                }

                .route-line .line {
                    width: 32px;
                    height: 2px;
                    background: #e5e7eb;
                    position: relative;
                }

                .route-line .line::after {
                    content: '';
                    position: absolute;
                    right: -4px;
                    top: -3px;
                    border-left: 6px solid #e5e7eb;
                    border-top: 4px solid transparent;
                    border-bottom: 4px solid transparent;
                }

                .flight-meta {
                    display: flex;
                    gap: 20px;
                    align-items: center;
                }

                .meta-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .meta-item .label {
                    font-size: 10px;
                    text-transform: uppercase;
                    color: #9ca3af;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }

                .meta-item .value {
                    font-size: 13px;
                    font-weight: 600;
                    color: #1a1a2e;
                    margin-top: 2px;
                }

                .flight-status-modern {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 4px 12px 4px 8px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                }

                .status-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                }

                .flight-actions {
                    display: flex;
                    gap: 4px;
                }

                .action-btn-modern {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    border: none;
                    background: transparent;
                    color: #6b7280;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .action-btn-modern:hover {
                    background: #f3f4f6;
                    color: #1a1a2e;
                }

                .action-btn-modern.delete:hover {
                    background: #fef2f2;
                    color: #dc2626;
                }

                .action-btn-modern.edit:hover {
                    background: #eff6ff;
                    color: #2563eb;
                }

                .empty-state-modern {
                    text-align: center;
                    padding: 60px 20px;
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f0f0f0;
                }

                .empty-state-modern svg {
                    color: #d1d5db;
                    margin-bottom: 16px;
                }

                .empty-state-modern h3 {
                    font-size: 20px;
                    font-weight: 600;
                    color: #1a1a2e;
                    margin: 0 0 8px 0;
                }

                .empty-state-modern p {
                    color: #6b7280;
                    font-size: 14px;
                    margin: 0;
                }

                .loading-state-modern {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f0f0f0;
                    gap: 16px;
                }

                .spinner-modern {
                    animation: spin 0.8s linear infinite;
                    color: #294b5a;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .error-box-modern {
                    padding: 14px 18px;
                    background: #fef2f2;
                    color: #dc2626;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 500;
                    border: 1px solid #fecaca;
                    margin-bottom: 20px;
                }

                /* ============================================
                   STYLES DES MODALS
                   ============================================ */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(0, 0, 0, 0.5);
                    backdrop-filter: blur(8px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                    animation: fadeIn 0.2s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from { 
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }

                .modal-content {
                    background: #ffffff;
                    border-radius: 20px;
                    max-width: 650px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 25px 50px rgba(0,0,0,0.2);
                    padding: 32px;
                    position: relative;
                    animation: slideUp 0.25s ease;
                }

                .modal-content.delete-modal {
                    max-width: 440px;
                    text-align: center;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding-bottom: 16px;
                    border-bottom: 1px solid #edf2f4;
                    margin-bottom: 4px;
                }

                .modal-header h3 {
                    margin: 0;
                    font-size: 20px;
                    color: #1a1a2e;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-weight: 700;
                }

                .modal-close-btn {
                    background: none;
                    border: none;
                    color: #6b7280;
                    cursor: pointer;
                    padding: 6px;
                    border-radius: 50%;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .modal-close-btn:hover {
                    background: #f3f4f6;
                    color: #1a1a2e;
                }

                .modal-body {
                    margin-top: 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .modal-section {
                    background: #f8fafc;
                    padding: 18px 20px;
                    border-radius: 12px;
                    border: 1px solid #eef2f6;
                }

                .modal-section-title {
                    font-size: 12px;
                    font-weight: 700;
                    color: #6b7280;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    margin-bottom: 14px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .details-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px 20px;
                }

                .detail-item {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .detail-label {
                    font-size: 11px;
                    color: #9ca3af;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .detail-value {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1a1a2e;
                }

                .badge-status {
                    display: inline-block;
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 600;
                }

                .badge-confirmed { background: #e6f4ea; color: #1e7e34; }
                .badge-pending { background: #fef3d6; color: #b25900; }
                .badge-processing { background: #e8f0fe; color: #1a73e8; }
                .badge-cancelled { background: #fce8e6; color: #c5221f; }
                .badge-completed { background: #f0ece4; color: #294b5a; }

                .delete-icon-wrapper {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    background: #fef2f2;
                    color: #dc2626;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 16px auto;
                }

                .delete-modal-title {
                    font-size: 20px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 8px 0;
                }

                .delete-modal-desc {
                    font-size: 14px;
                    color: #6b7280;
                    margin: 0 0 20px 0;
                    line-height: 1.6;
                }

                .delete-flight-summary {
                    background: #f8fafc;
                    padding: 12px 16px;
                    border-radius: 12px;
                    margin-bottom: 24px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #1a1a2e;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border: 1px solid #eef2f6;
                }

                .delete-actions {
                    display: flex;
                    gap: 12px;
                }

                .btn-cancel {
                    flex: 1;
                    padding: 12px 16px;
                    border: 1px solid #e5e7eb;
                    background: #ffffff;
                    color: #374151;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .btn-cancel:hover {
                    background: #f9fafb;
                    border-color: #d1d5db;
                }

                .btn-cancel:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .btn-delete-confirm {
                    flex: 1;
                    padding: 12px 16px;
                    border: none;
                    background: #dc2626;
                    color: #ffffff;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: all 0.2s ease;
                }

                .btn-delete-confirm:hover {
                    background: #b91c1c;
                }

                .btn-delete-confirm:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .loading-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 40px 20px;
                    color: #6b7280;
                    gap: 12px;
                }

                .spinner {
                    animation: spin 0.8s linear infinite;
                }

                .error-box {
                    padding: 12px 16px;
                    background: #fef2f2;
                    color: #dc2626;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 500;
                    border: 1px solid #fecaca;
                }

                .modal-content::-webkit-scrollbar {
                    width: 6px;
                }

                .modal-content::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 3px;
                }

                .modal-content::-webkit-scrollbar-thumb {
                    background: #d1d5db;
                    border-radius: 3px;
                }

                .modal-content::-webkit-scrollbar-thumb:hover {
                    background: #9ca3af;
                }

                @media (max-width: 1024px) {
                    .flight-card {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }

                    .flight-meta {
                        flex-wrap: wrap;
                        gap: 12px;
                    }
                }

                @media (max-width: 768px) {
                    .flights-page {
                        padding: 0 16px 30px;
                    }

                    .page-header-modern {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .header-actions {
                        flex-wrap: wrap;
                    }

                    .stats-grid-modern {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .filters-modern {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .search-modern {
                        min-width: unset;
                    }

                    .filter-group {
                        justify-content: center;
                    }

                    .flight-card-left {
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .flight-route-modern {
                        width: 100%;
                        justify-content: space-between;
                    }

                    .flight-actions {
                        justify-content: flex-end;
                    }

                    .modal-content {
                        padding: 24px 20px;
                        margin: 10px;
                        max-height: 95vh;
                    }

                    .details-grid {
                        grid-template-columns: 1fr;
                        gap: 10px;
                    }

                    .delete-actions {
                        flex-direction: column;
                    }

                    .modal-header h3 {
                        font-size: 18px;
                    }
                }

                @media (max-width: 480px) {
                    .stats-grid-modern {
                        grid-template-columns: 1fr 1fr;
                        gap: 10px;
                    }

                    .stat-card-modern {
                        padding: 14px 16px;
                    }

                    .stat-content-modern h3 {
                        font-size: 20px;
                    }
                }

                /* ============================================
   MODAL MODERN - WIDE VERSION AVEC SCROLL
   ============================================ */
.modal-content-wide {
    background: #ffffff;
    border-radius: 24px;
    max-width: 820px;
    width: 100%;
    max-height: 85vh;
    box-shadow: 0 25px 60px rgba(0,0,0,0.2);
    position: relative;
    animation: slideUp 0.3s ease;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Header fixe - ne scroll pas */
.modal-header-fixed {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 32px 16px 32px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
    background: #ffffff;
    z-index: 10;
}

.modal-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
}

.modal-header-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #e8f0fe;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1a73e8;
    flex-shrink: 0;
}

.modal-header-left h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #1a1a2e;
}

.modal-subtitle {
    font-size: 13px;
    color: #6b7280;
    font-weight: 500;
}

.modal-close-btn {
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    padding: 8px;
    border-radius: 50%;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.modal-close-btn:hover {
    background: #f3f4f6;
    color: #1a1a2e;
}

/* Body scrollable */
.modal-body-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 20px 32px 32px 32px;
}

/* Scrollbar personnalisée pour le body */
.modal-body-scroll::-webkit-scrollbar {
    width: 4px;
}

.modal-body-scroll::-webkit-scrollbar-track {
    background: transparent;
}

.modal-body-scroll::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
}

.modal-body-scroll::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
}

/* Status Bar */
.status-bar {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    background: #f8fafc;
    padding: 14px 18px;
    border-radius: 14px;
    border: 1px solid #eef2f6;
    margin-bottom: 20px;
}

.status-bar-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.status-label {
    font-size: 10px;
    text-transform: uppercase;
    color: #9ca3af;
    font-weight: 600;
    letter-spacing: 0.5px;
}

.status-value {
    font-size: 15px;
    font-weight: 700;
    color: #1a1a2e;
}

/* Route Section */
.route-section {
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
    border: 1px solid #eef2f6;
    border-radius: 16px;
    padding: 20px 24px;
    margin-bottom: 20px;
}

.route-display {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.route-point-lg {
    text-align: center;
    flex: 1;
}

.route-point-lg .route-city {
    font-size: 20px;
    font-weight: 700;
    color: #1a1a2e;
}

.route-point-lg .route-code {
    font-size: 13px;
    color: #6b7280;
    font-weight: 600;
    margin-top: 2px;
}

.route-point-lg .route-airport {
    font-size: 12px;
    color: #9ca3af;
    margin-top: 2px;
}

.route-arrow {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 60px;
}

.route-line-lg {
    width: 40px;
    height: 2px;
    background: #d1d5db;
    position: relative;
}

.route-line-lg::after {
    content: '';
    position: absolute;
    right: -5px;
    top: -4px;
    border-left: 7px solid #d1d5db;
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
}

.route-arrow-icon {
    color: #9ca3af;
    margin-top: 4px;
}

.route-dates {
    display: flex;
    justify-content: center;
    gap: 32px;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #eef2f6;
}

.date-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #6b7280;
}

.date-item strong {
    color: #1a1a2e;
    font-weight: 600;
}

.date-item svg {
    color: #6b7280;
}

/* Details Grid Modern */
.details-grid-modern {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
}

.details-col {
    background: #f8fafc;
    border-radius: 14px;
    padding: 16px 18px;
    border: 1px solid #eef2f6;
}

.details-col-title {
    font-size: 11px;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eef2f6;
}

.details-col-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 16px;
}

.detail-item-modern {
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.detail-label-modern {
    font-size: 10px;
    color: #9ca3af;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.detail-value-modern {
    font-size: 13px;
    font-weight: 600;
    color: #1a1a2e;
    word-break: break-word;
}

/* Passengers Summary */
.passengers-summary {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    padding-top: 12px;
    border-top: 1px solid #eef2f6;
}

.passenger-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    background: #eef2f6;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    color: #1a1a2e;
}

.passenger-badge svg {
    color: #6b7280;
}

/* Badge Status */
.badge-status {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
}

.badge-confirmed { background: #e6f4ea; color: #1e7e34; }
.badge-pending { background: #fef3d6; color: #b25900; }
.badge-processing { background: #e8f0fe; color: #1a73e8; }
.badge-cancelled { background: #fce8e6; color: #c5221f; }
.badge-completed { background: #f0ece4; color: #294b5a; }

/* Loading State */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #6b7280;
    gap: 12px;
    flex: 1;
}

.spinner {
    animation: spin 0.8s linear infinite;
}

/* Responsive */
@media (max-width: 768px) {
    .modal-content-wide {
        max-width: 100%;
        margin: 10px;
        max-height: 92vh;
        border-radius: 16px;
    }

    .modal-header-fixed {
        padding: 16px 16px 12px 16px;
    }

    .modal-body-scroll {
        padding: 12px 16px 20px 16px;
    }

    .status-bar {
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
        padding: 12px 14px;
    }

    .details-grid-modern {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .details-col-grid {
        grid-template-columns: 1fr 1fr;
        gap: 8px 12px;
    }

    .route-display {
        flex-direction: column;
        gap: 8px;
    }

    .route-arrow {
        flex-direction: row;
        flex: 0 0 auto;
        gap: 8px;
    }

    .route-line-lg {
        width: 60px;
        height: 2px;
    }

    .route-line-lg::after {
        right: auto;
        left: -5px;
        border-left: none;
        border-right: 7px solid #d1d5db;
        border-top: 5px solid transparent;
        border-bottom: 5px solid transparent;
    }

    .route-arrow-icon {
        transform: rotate(0deg);
    }

    .route-dates {
        flex-direction: column;
        gap: 8px;
        align-items: center;
    }

    .modal-header-left h3 {
        font-size: 17px;
    }

    .modal-header-icon {
        width: 36px;
        height: 36px;
    }

    .modal-header-icon svg {
        width: 18px;
        height: 18px;
    }
}

@media (max-width: 480px) {
    .modal-content-wide {
        margin: 6px;
        border-radius: 12px;
        max-height: 95vh;
    }

    .modal-header-fixed {
        padding: 12px 12px 10px 12px;
    }

    .modal-body-scroll {
        padding: 10px 12px 16px 12px;
    }

    .status-bar {
        grid-template-columns: 1fr 1fr;
        padding: 10px 12px;
        gap: 6px;
    }

    .details-col-grid {
        grid-template-columns: 1fr;
        gap: 8px;
    }

    .route-point-lg .route-city {
        font-size: 17px;
    }

    .route-section {
        padding: 14px 16px;
    }

    .modal-header-left {
        gap: 10px;
    }

    .modal-header-left h3 {
        font-size: 15px;
    }

    .modal-subtitle {
        font-size: 11px;
    }
}
            `}</style>

            <div className="flights-page">
                {/* Header */}
                <div className="page-header-modern">
                    <div>
                        <h2 style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '28px', fontWeight: 700, margin: 0 }}>
                            <Plane size={32} style={{ color: '#294b5a' }} />
                            My Flight Bookings
                        </h2>
                        <p>Manage and track all your flight reservations in one place</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-refresh" onClick={() => window.location.reload()}>
                            <RefreshCw size={16} />
                            Refresh
                        </button>
                    </div>
                </div>

                {error && <div className="error-box-modern">{error}</div>}

                {/* Stats */}
                <div className="stats-grid-modern">
                    <div className="stat-card-modern">
                        <div className="stat-icon-modern" style={{ background: '#e8f0fe', color: '#1a73e8' }}>
                            <Plane size={24} />
                        </div>
                        <div className="stat-content-modern">
                            <h3>{totalFlights}</h3>
                            <p>Total Bookings</p>
                        </div>
                    </div>

                    <div className="stat-card-modern">
                        <div className="stat-icon-modern" style={{ background: '#fef3d6', color: '#b25900' }}>
                            <Clock size={24} />
                        </div>
                        <div className="stat-content-modern">
                            <h3>{upcomingFlights}</h3>
                            <p>Upcoming Flights</p>
                        </div>
                    </div>

                    <div className="stat-card-modern">
                        <div className="stat-icon-modern" style={{ background: '#e6f4ea', color: '#1e7e34' }}>
                            <CreditCard size={24} />
                        </div>
                        <div className="stat-content-modern">
                            <h3>{completedFlights}</h3>
                            <p>Completed Flights</p>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="filters-modern">
                    <div className="search-modern">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Search by airline, route, or reference..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="filter-group">
                        {["All Flights", "Upcoming", "Completed", "Cancelled"].map((filter) => (
                            <button
                                key={filter}
                                className={`filter-btn-modern ${activeFilter === filter ? "active" : ""}`}
                                onClick={() => setActiveFilter(filter)}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Flight Cards */}
                {loading ? (
                    <div className="loading-state-modern">
                        <Loader2 className="spinner-modern" size={36} />
                        <span style={{ color: '#6b7280' }}>Loading your flights...</span>
                    </div>
                ) : filteredFlights.length > 0 ? (
                    <div className="flights-grid">
                        {filteredFlights.map((flight) => {
                            const statusColor = getStatusColor(flight.status);
                            return (
                                <div key={flight.id} className="flight-card">
                                    <div className="flight-card-left">
                                        <div className="flight-airline-icon">
                                            <Plane size={20} color="#294b5a" />
                                        </div>
                                        <div className="flight-info">
                                            <h4 style={{ color: flight.airline === "Airline pending assignment" ? '#b25900' : '#1a1a2e' }}>
                                                {flight.airline}
                                            </h4>
                                            <span className="flight-ref">Ref: {flight.flightNumber}</span>
                                        </div>
                                    </div>

                                    <div className="flight-route-modern">
                                        <div className="route-point">
                                            <div className="city">{airportCode(flight.from)}</div>
                                            <div className="code">{flight.from?.split(',')[0]}</div>
                                        </div>
                                        <div className="route-line">
                                            <div className="line" />
                                        </div>
                                        <div className="route-point">
                                            <div className="city">{airportCode(flight.to)}</div>
                                            <div className="code">{flight.to?.split(',')[0]}</div>
                                        </div>
                                    </div>

                                    <div className="flight-meta">
                                        <div className="meta-item">
                                            <span className="label">Departure</span>
                                            <span className="value">{formatDate(flight.departureDate)}</span>
                                        </div>
                                        <div className="meta-item">
                                            <span className="label">Passengers</span>
                                            <span className="value">{flight.passengers}</span>
                                        </div>
                                        <div className="meta-item">
                                            <span className="label">Class</span>
                                            <span className="value">{flight.class}</span>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div 
                                            className="flight-status-modern"
                                            style={{ background: statusColor.bg, color: statusColor.text }}
                                        >
                                            <span className="status-dot" style={{ background: statusColor.dot }} />
                                            {flight.status}
                                        </div>

                                        <div className="flight-actions">
                                            <button 
                                                className="action-btn-modern" 
                                                title="View Details"
                                                onClick={() => handleViewDetails(flight.id)}
                                            >
                                                <Eye size={16} />
                                            </button>
                                            
                                            {flight.status === "Pending" && (
                                                <>
                                                    <button
                                                        className="action-btn-modern"
                                                        title="Cancel booking"
                                                        onClick={() => openCancelModal(flight)}
                                                    >
                                                        <Ban size={16} />
                                                    </button>
                                                </>
                                            )}

                                            <button
                                                className="action-btn-modern delete"
                                                title="Delete"
                                                onClick={() => openDeleteModal(flight)}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="empty-state-modern">
                        <Plane size={48} />
                        <h3>No flights found</h3>
                        <p>Try adjusting your search or filter criteria</p>
                    </div>
                )}
            </div>

  {/* Flight Details Modal - Version avec scroll uniquement sur le contenu */}
{isModalOpen && (
    <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
        <div className="modal-content-wide" onClick={(e) => e.stopPropagation()}>
            {/* Header fixe - ne scroll pas */}
            <div className="modal-header-fixed">
                <div className="modal-header-left">
                    <div className="modal-header-icon">
                        <Plane size={22} />
                    </div>
                    <div>
                        <h3>Flight Details</h3>
                        <span className="modal-subtitle">Reference: {selectedFlight?.referenceCode}</span>
                    </div>
                </div>
                <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}>
                    <X size={20} />
                </button>
            </div>

            {modalLoading ? (
                <div className="loading-state">
                    <Loader2 className="spinner" size={32} />
                    <span>Fetching flight details...</span>
                </div>
            ) : selectedFlight && (
                /* Contenu scrollable */
                <div className="modal-body-scroll">
                    {/* Status Bar */}
                    <div className="status-bar">
                        <div className="status-bar-item">
                            <span className="status-label">Status</span>
                            <span className={`badge-status badge-${(selectedFlight.status ?? "pending").toLowerCase()}`}>
                                {selectedFlight.status ?? "Pending"}
                            </span>
                        </div>
                        <div className="status-bar-item">
                            <span className="status-label">Price</span>
                            <span className="status-value">
                                {selectedFlight.amount !== null ? `$${selectedFlight.amount.toLocaleString()}` : "Quote pending"}
                            </span>
                        </div>
                        <div className="status-bar-item">
                            <span className="status-label">Class</span>
                            <span className="status-value">{selectedFlight.class}</span>
                        </div>
                        <div className="status-bar-item">
                            <span className="status-label">Trip Type</span>
                            <span className="status-value">{selectedFlight.tripType}</span>
                        </div>
                    </div>

                    {/* Route Section */}
                    <div className="route-section">
                        <div className="route-display">
                            <div className="route-point-lg">
                                <div className="route-city">{selectedFlight.from?.split(',')[0] || "N/A"}</div>
                                <div className="route-code">{airportCode(selectedFlight.from)}</div>
                                <div className="route-airport">{selectedFlight.from ?? "N/A"}</div>
                            </div>
                            <div className="route-arrow">
                                <div className="route-line-lg"></div>
                                <ArrowRight size={20} className="route-arrow-icon" />
                            </div>
                            <div className="route-point-lg">
                                <div className="route-city">{selectedFlight.to?.split(',')[0] || "N/A"}</div>
                                <div className="route-code">{airportCode(selectedFlight.to)}</div>
                                <div className="route-airport">{selectedFlight.to ?? "N/A"}</div>
                            </div>
                        </div>
                        <div className="route-dates">
                            <div className="date-item">
                                <Calendar size={14} />
                                <span>Departure: <strong>{formatDate(selectedFlight.departureDate)}</strong></span>
                            </div>
                            {selectedFlight.returnDate && (
                                <div className="date-item">
                                    <Calendar size={14} />
                                    <span>Return: <strong>{formatDate(selectedFlight.returnDate)}</strong></span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Two Column Grid */}
                    <div className="details-grid-modern">
                        {/* Left Column - Flight Info */}
                        <div className="details-col">
                            <div className="details-col-title">
                                <Info size={14} />
                                Flight Information
                            </div>
                            <div className="details-col-grid">
                                <div className="detail-item-modern">
                                    <span className="detail-label-modern">Reference</span>
                                    <span className="detail-value-modern">{selectedFlight.referenceCode}</span>
                                </div>
                                <div className="detail-item-modern">
                                    <span className="detail-label-modern">Airline</span>
                                    <span className="detail-value-modern">{selectedFlight.airline}</span>
                                </div>
                                <div className="detail-item-modern">
                                    <span className="detail-label-modern">Via</span>
                                    <span className="detail-value-modern">{selectedFlight.via}</span>
                                </div>
                                <div className="detail-item-modern">
                                    <span className="detail-label-modern">Flexibility</span>
                                    <span className="detail-value-modern">{selectedFlight.flexibility}</span>
                                </div>
                                <div className="detail-item-modern">
                                    <span className="detail-label-modern">Baggage</span>
                                    <span className="detail-value-modern">{selectedFlight.baggage}</span>
                                </div>
                                <div className="detail-item-modern">
                                    <span className="detail-label-modern">Special Requests</span>
                                    <span className="detail-value-modern">{selectedFlight.specialRequests}</span>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Passenger Info */}
                        <div className="details-col">
                            <div className="details-col-title">
                                <User size={14} />
                                Passenger Information
                            </div>
                            <div className="details-col-grid">
                                <div className="detail-item-modern">
                                    <span className="detail-label-modern">Full Name</span>
                                    <span className="detail-value-modern">{selectedFlight.passenger.firstName} {selectedFlight.passenger.lastName}</span>
                                </div>
                                <div className="detail-item-modern">
                                    <span className="detail-label-modern">Email</span>
                                    <span className="detail-value-modern">{selectedFlight.passenger.email}</span>
                                </div>
                                <div className="detail-item-modern">
                                    <span className="detail-label-modern">Phone</span>
                                    <span className="detail-value-modern">{selectedFlight.passenger.phone}</span>
                                </div>
                                <div className="detail-item-modern">
                                    <span className="detail-label-modern">Nationality</span>
                                    <span className="detail-value-modern">{selectedFlight.passenger.nationality}</span>
                                </div>
                                <div className="detail-item-modern">
                                    <span className="detail-label-modern">Passport</span>
                                    <span className="detail-value-modern">{selectedFlight.passenger.passportNumber}</span>
                                </div>
                                <div className="detail-item-modern">
                                    <span className="detail-label-modern">Passport Expiry</span>
                                    <span className="detail-value-modern">{formatDate(selectedFlight.passenger.passportExpiry)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Passengers Summary */}
                    <div className="passengers-summary">
                        <div className="passenger-badge">
                            <Users size={14} />
                            <span>{selectedFlight.adults} Adult{selectedFlight.adults > 1 ? 's' : ''}</span>
                        </div>
                        {selectedFlight.children > 0 && (
                            <div className="passenger-badge">
                                <Users size={14} />
                                <span>{selectedFlight.children} Child{selectedFlight.children > 1 ? 'ren' : ''}</span>
                            </div>
                        )}
                        {selectedFlight.infants > 0 && (
                            <div className="passenger-badge">
                                <Users size={14} />
                                <span>{selectedFlight.infants} Infant{selectedFlight.infants > 1 ? 's' : ''}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    </div>
)}

            {/* Cancel Modal */}
            {isCancelModalOpen && flightToCancel && (
                <div className="modal-overlay" onClick={() => !isCancelling && setIsCancelModalOpen(false)}>
                    <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-icon-wrapper">
                            <AlertTriangle size={30} />
                        </div>

                        <h3 className="delete-modal-title">Cancel this booking?</h3>
                        <p className="delete-modal-desc">
                            This will cancel your flight request. You can always submit a new request afterwards, but this specific booking cannot be reactivated.
                        </p>

                        <div className="delete-flight-summary">
                            <span>{flightToCancel.airline ?? "Unknown airline"} ({flightToCancel.flightNumber ?? "N/A"})</span>
                        </div>

                        {cancelError && (
                            <div className="error-box" style={{ marginBottom: "16px" }}>
                                {cancelError}
                            </div>
                        )}

                        <div className="delete-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => setIsCancelModalOpen(false)}
                                disabled={isCancelling}
                            >
                                Keep Booking
                            </button>
                            <button
                                className="btn-delete-confirm"
                                onClick={confirmCancel}
                                disabled={isCancelling}
                            >
                                {isCancelling ? (
                                    <>
                                        <Loader2 className="spinner" size={16} /> Cancelling...
                                    </>
                                ) : (
                                    "Cancel Booking"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {isDeleteModalOpen && flightToDelete && (
                <div className="modal-overlay" onClick={() => !isDeleting && setIsDeleteModalOpen(false)}>
                    <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-icon-wrapper">
                            <AlertTriangle size={30} />
                        </div>

                        <h3 className="delete-modal-title">Delete Booking?</h3>
                        <p className="delete-modal-desc">
                            Are you sure you want to permanently delete this flight booking? This action cannot be undone.
                        </p>

                        <div className="delete-flight-summary">
                            <span>{flightToDelete.airline ?? "Unknown airline"} ({flightToDelete.flightNumber ?? "N/A"})</span>
                        </div>

                        {deleteError && (
                            <div className="error-box" style={{ marginBottom: "16px" }}>
                                {deleteError}
                            </div>
                        )}

                        <div className="delete-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => setIsDeleteModalOpen(false)}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-delete-confirm"
                                onClick={confirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <Loader2 className="spinner" size={16} /> Deleting...
                                    </>
                                ) : (
                                    "Delete Flight"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal - à ajouter si nécessaire */}
        </CustomerLayout>
    );
};

export default MyFlights;